/**
 * Material UI Theme Configuration
 * 
 */

import { createTheme } from '@mui/material/styles';

import { colors } from './colors';
import { FONT_FAMILIES, FONT_SIZES, FONT_WEIGHTS, LETTER_SPACING, LINE_HEIGHTS } from './constants';


// Create the theme with design tokens
export const theme = createTheme({
  palette: {
    primary: {
      main: '#165A8A',
      light: colors.blue[100],
      dark: '#134a75',
      contrastText: colors.contrast[900],
    },
    secondary: {
      main: colors.surface[1000],
      light: colors.neutral[100],
      dark: colors.neutral[900],
      contrastText: colors.neutral[700],
    },
    background: {
      default: colors.surface[800],
      paper: colors.surface[1000],

    },
    text: {
      primary: colors.neutral[1000],
      secondary: colors.neutral[900],
      disabled: colors.neutral[200],
    },
    error: {
      main: colors.red[1000],
      light: colors.red[100],
      dark: colors.red[900],
      contrastText: colors.contrast[900],
    },
    warning: {
      main: colors.amber[1000],
      light: colors.amber[100],
      dark: colors.amber[900],
      contrastText: colors.contrast[900],
    },
    info: {
      main: colors.purple[1000],
      light: colors.purple[100],
      dark: colors.purple[900],
      contrastText: colors.contrast[900],
    },
    success: {
      main: colors.green[1000],
      light: colors.green[100],
      dark: colors.green[900],
      contrastText: colors.contrast[900],
    },
    divider: colors.neutral[200],
  },
  typography: {
    fontFamily: FONT_FAMILIES.primary,

  h1: {
    fontWeight: FONT_WEIGHTS.REGULAR,
    fontSize: FONT_SIZES.XXXL,
    lineHeight: LINE_HEIGHTS.TIGHT,
    letterSpacing: LETTER_SPACING.NONE,
  },
  h2: {
    fontWeight: FONT_WEIGHTS.SEMIBOLD,
    fontSize: FONT_SIZES.XXL,
    lineHeight: LINE_HEIGHTS.COMPACT,
    letterSpacing: LETTER_SPACING.NONE,
  },
  h3: {
    fontWeight: FONT_WEIGHTS.MEDIUM,
    fontSize: FONT_SIZES.XL,
    lineHeight: 1.32, // Unique ratio; can be tokenized if reused
    letterSpacing: LETTER_SPACING.NEG_LG,
  },
  h4: {
    fontWeight: FONT_WEIGHTS.REGULAR,
    fontSize: FONT_SIZES.LG,
    lineHeight: LINE_HEIGHTS.SNUG,
    letterSpacing: LETTER_SPACING.POS_LG,
    textTransform: "uppercase",
  },
  h5: {
    fontWeight: FONT_WEIGHTS.MEDIUM,
    fontSize: FONT_SIZES.LG,
    lineHeight: LINE_HEIGHTS.SNUG,
    letterSpacing: LETTER_SPACING.NEG_LG,
  },
  h6: {
    fontWeight: FONT_WEIGHTS.MEDIUM,
    fontSize: FONT_SIZES.MD,
    lineHeight: LINE_HEIGHTS.RELAXED,
    letterSpacing: LETTER_SPACING.NONE,
  },
  body1: {
    fontWeight: FONT_WEIGHTS.REGULAR,
    fontSize: FONT_SIZES.MD,
    lineHeight: LINE_HEIGHTS.RELAXED,
    letterSpacing: LETTER_SPACING.NONE,
  },
  body2: {
    fontWeight: FONT_WEIGHTS.REGULAR,
    fontSize: FONT_SIZES.SM,
    lineHeight: LINE_HEIGHTS.SNUG,
    letterSpacing: LETTER_SPACING.NONE,
  },
  caption: {
    fontWeight: FONT_WEIGHTS.REGULAR,
    fontSize: FONT_SIZES.SM,
    lineHeight: LINE_HEIGHTS.SNUG,
    letterSpacing: LETTER_SPACING.NONE,
  },
  button: {
    fontWeight: FONT_WEIGHTS.SEMIBOLD,
    fontSize: FONT_SIZES.BASE,
    lineHeight: LINE_HEIGHTS.LOOSE,
    letterSpacing: LETTER_SPACING.NEG_MD,
    textTransform: "none",
  },
  overline: {
    fontWeight: FONT_WEIGHTS.REGULAR,
    fontSize: FONT_SIZES.XS,
    lineHeight: LINE_HEIGHTS.LOOSE,
    textTransform: "uppercase",
    letterSpacing: LETTER_SPACING.POS_SM,
  },
  },
  spacing: 8, // 8px base unit
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        html, body {
          font-family: 'IBM Plex Sans', 'Roboto', sans-serif;
        }
      `,
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          maxWidth: 500,
        },
      },
    },
    MuiChip: {
      variants: [
        {
          props: { variant: 'filled' as const },
          style: {
            '&.monospace': {
              fontFamily: FONT_FAMILIES.monospace,
            },
          },
        },
      ],
    },
    //
    MuiTypography: {
      variants: [
        {
          props: { variant: 'body1' as const },
          style: {
            '&.content-width': {
              maxWidth: 600,
            },
            '&.narrow-content': {
              maxWidth: 500,
            },
          },
        },
        {
          props: { variant: 'caption' as const },
          style: {
            '&.italic': {
              fontStyle: 'italic',
            },
          },
        },
      ],
    },
  },
});
